/**
 * Accordion showing proposed prompt changes with inline diffs.
 *
 * Each accordion item shows:
 *  - Section name + action badge + edit/delete icons
 *  - Rationale
 *  - Inline word-level diff (original vs new)
 *  - Inline text editor when editing
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import InlineDiff from './InlineDiff';
import type { PromptChange } from '@/lib/api/versionWorkflow';

interface Props {
  changes: PromptChange[];
  onSaveEdit?: (changeId: string, newText: string) => void;
  onDelete?: (changeId: string) => void;
  readOnly?: boolean;
}

function actionBadge(action: string) {
  const lower = action.toLowerCase();
  if (lower.includes('add') || lower.includes('insert')) {
    return <Badge className="bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400 border-green-200 text-[10px]">Added</Badge>;
  }
  if (lower.includes('remove') || lower.includes('delete')) {
    return <Badge className="bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400 border-red-200 text-[10px]">Removed</Badge>;
  }
  return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400 border-blue-200 text-[10px]">Modified</Badge>;
}

const ChangesAccordion: React.FC<Props> = ({ changes, onSaveEdit, onDelete, readOnly }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  if (changes.length === 0) return null;

  const startEditing = (change: PromptChange) => {
    setEditingId(change.change_id);
    setEditText(change.new_text ?? change.original_text ?? '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEditing = (changeId: string) => {
    onSaveEdit?.(changeId, editText);
    setEditingId(null);
    setEditText('');
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-heading">Suggested Changes</h3>
      <Accordion type="multiple" className="space-y-2">
        {changes.map((change, idx) => {
          const isEditing = editingId === change.change_id;

          return (
            <motion.div
              key={change.change_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.25 }}
            >
              <AccordionItem
                value={change.change_id}
                className="rounded-xl border border-border/60 bg-card overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-3 text-sm hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2 text-left">
                      <span className="font-medium text-heading">{change.section_name}</span>
                      {actionBadge(change.action)}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-3">
                      {!readOnly && onSaveEdit && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startEditing(change); }}
                          className="p-1 rounded-md text-muted-foreground hover:text-heading hover:bg-muted/60 transition-colors"
                          aria-label={`Edit ${change.section_name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {!readOnly && onDelete && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDelete(change.change_id); }}
                          className="p-1 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          aria-label={`Delete ${change.section_name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-3">
                  {/* Rationale */}
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {change.rationale}
                  </p>

                  {isEditing && !readOnly ? (
                    /* ── Inline editor ─────────────────────────── */
                    <div className="space-y-3">
                      {/* Original text (read-only) */}
                      {change.original_text && (
                        <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                          <pre className="font-mono text-xs text-muted-foreground whitespace-pre-wrap">
                            {change.original_text}
                          </pre>
                        </div>
                      )}

                      {/* Editable new text */}
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full rounded-lg border border-border bg-background p-3 font-mono text-xs text-body leading-relaxed resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/40"
                        rows={6}
                      />

                      {/* Cancel / Update buttons */}
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={cancelEditing}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => saveEditing(change.change_id)}>
                          Update
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* ── Read-only diff view ──────────────────── */
                    <>
                      {/* Inline diff */}
                      {change.original_text && change.new_text && (
                        <div className="rounded-lg border border-border/40 bg-muted/20 p-3 overflow-x-auto">
                          <InlineDiff
                            original={change.original_text}
                            modified={change.new_text}
                          />
                        </div>
                      )}

                      {/* New text only (addition) */}
                      {!change.original_text && change.new_text && (
                        <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                          <pre className="font-mono text-xs text-green-700 dark:text-green-400 whitespace-pre-wrap">
                            {change.new_text}
                          </pre>
                        </div>
                      )}

                      {/* Original text only (removal) */}
                      {change.original_text && !change.new_text && (
                        <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                          <pre className="font-mono text-xs text-red-700 dark:text-red-400 whitespace-pre-wrap line-through">
                            {change.original_text}
                          </pre>
                        </div>
                      )}
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          );
        })}
      </Accordion>
    </div>
  );
};

export default ChangesAccordion;
